import React from 'react';
import { createRoot } from 'react-dom/client';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import JobCardPrintSheet from './JobCardPrintSheet';
import { DEFAULT_COMPANY_INFO } from '../../../utils/companyInfo';

const wait = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

export const buildJobCardFilename = (jobCard) => {
  const plateNo = String(jobCard?.plate_no || 'jobcard').replace(/\s+/g, '_');
  const cardNo = String(jobCard?.job_card_no || jobCard?.id || 'new').replace(/[:\s]+/g, '_');
  const datePart = jobCard?.opened_date
    ? new Date(jobCard.opened_date).toISOString().split('T')[0]
    : new Date().toISOString().split('T')[0];
  return `JobCard_${plateNo}_${cardNo}_${datePart}.pdf`;
};

const printBlobUrl = (blobUrl) => new Promise((resolve) => {
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.style.visibility = 'hidden';
  iframe.src = blobUrl;

  const cleanup = () => {
    if (iframe.parentNode) {
      iframe.parentNode.removeChild(iframe);
    }
    resolve();
  };

  iframe.onload = () => {
    try {
      const frameWindow = iframe.contentWindow;
      if (frameWindow) {
        frameWindow.onafterprint = cleanup;
        frameWindow.focus();
        frameWindow.print();
        window.setTimeout(cleanup, 15000);
      } else {
        window.open(blobUrl, '_blank', 'noopener,noreferrer');
        cleanup();
      }
    } catch (error) {
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
      cleanup();
    }
  };

  document.body.appendChild(iframe);
});

const renderTempPrintSheet = async (jobCard, companyInfo = DEFAULT_COMPANY_INFO, generatedBy = '') => {
  let printElement = document.getElementById('job-card-print-sheet');
  let tempHost = null;
  let root = null;

  if (!printElement) {
    tempHost = document.createElement('div');
    tempHost.setAttribute('aria-hidden', 'true');
    tempHost.style.position = 'fixed';
    tempHost.style.left = '-10000px';
    tempHost.style.top = '0';
    tempHost.style.width = '210mm';
    tempHost.style.pointerEvents = 'none';
    tempHost.style.overflow = 'hidden';
    tempHost.style.background = '#ffffff';
    document.body.appendChild(tempHost);

    root = createRoot(tempHost);
    root.render(<JobCardPrintSheet jobCard={jobCard} companyInfo={companyInfo} generatedBy={generatedBy} />);
    await wait(350);
    printElement = tempHost.querySelector('#job-card-print-sheet');
  }

  if (!printElement) {
    throw new Error('Job card print sheet not found');
  }

  const cloneHost = document.createElement('div');
  cloneHost.setAttribute('aria-hidden', 'true');
  cloneHost.style.position = 'fixed';
  cloneHost.style.left = '-10000px';
  cloneHost.style.top = '0';
  cloneHost.style.width = `${printElement.scrollWidth}px`;
  cloneHost.style.pointerEvents = 'none';
  cloneHost.style.overflow = 'hidden';
  cloneHost.style.background = '#ffffff';

  const clone = printElement.cloneNode(true);
  clone.style.zoom = '1';
  clone.style.transform = 'none';
  clone.style.margin = '0';
  clone.style.maxWidth = 'none';
  clone.style.width = '210mm';

  cloneHost.appendChild(clone);
  document.body.appendChild(cloneHost);

  return {
    cloneHost,
    tempHost,
    root,
    clone
  };
};

const buildCanvasesFromClone = async (clone) => {
  const pageNodes = Array.from(clone.querySelectorAll('.job-card-pdf-page'));

  if (pageNodes.length > 0) {
    const canvases = [];
    for (const pageNode of pageNodes) {
      // eslint-disable-next-line no-await-in-loop
      const canvas = await html2canvas(pageNode, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        width: pageNode.scrollWidth,
        height: pageNode.scrollHeight,
        windowWidth: pageNode.scrollWidth,
        windowHeight: pageNode.scrollHeight
      });
      canvases.push(canvas);
    }
    return canvases;
  }

  return [await html2canvas(clone, {
    scale: 2,
    useCORS: true,
    logging: false,
    backgroundColor: '#ffffff',
    width: clone.scrollWidth,
    height: clone.scrollHeight,
    windowWidth: clone.scrollWidth,
    windowHeight: clone.scrollHeight
  })];
};

export const generateJobCardPDF = async (jobCard, companyInfo = DEFAULT_COMPANY_INFO, generatedBy = '') => {
  const { cloneHost, tempHost, root, clone } = await renderTempPrintSheet(jobCard, companyInfo, generatedBy);

  let canvases = [];
  try {
    canvases = await buildCanvasesFromClone(clone);
  } finally {
    if (root) {
      root.unmount();
    }
    if (cloneHost.parentNode) {
      cloneHost.parentNode.removeChild(cloneHost);
    }
    if (tempHost && tempHost.parentNode) {
      tempHost.parentNode.removeChild(tempHost);
    }
  }

  const pdf = new jsPDF('p', 'mm', 'a4');
  const imgWidth = 210;

  canvases.forEach((canvas, index) => {
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    const imgData = canvas.toDataURL('image/png', 1.0);

    if (index > 0) {
      pdf.addPage();
    }

    pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
  });

  return pdf;
};

export const downloadJobCardPDF = async (jobCard, companyInfo = DEFAULT_COMPANY_INFO, generatedBy = '') => {
  const pdf = await generateJobCardPDF(jobCard, companyInfo, generatedBy);
  pdf.save(buildJobCardFilename(jobCard));
  return pdf;
};

export const printJobCardPDF = async (jobCard, companyInfo = DEFAULT_COMPANY_INFO, generatedBy = '') => {
  const pdf = await generateJobCardPDF(jobCard, companyInfo, generatedBy);
  const blobUrl = pdf.output('bloburl');
  await printBlobUrl(blobUrl);
  return pdf;
};
